import { Flex } from '@radix-ui/themes';
import { RouteHelper } from 'classes/helpers/route.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ApiKeysManagementHoC } from 'components/sections/admin-portal/api-keys';
import { BallTypesTable } from 'components/sections/admin-portal/ball-types/table';
import { ErrorTypesTableHoc } from 'components/sections/admin-portal/error-types/table';
import { MachineModelsTableHoC } from 'components/sections/admin-portal/machine-models/table';
import { MachinesTable } from 'components/sections/admin-portal/machines/table';
import { ServerControlsTab } from 'components/sections/admin-portal/server-controls';
import { ServerErrorsTableHoC } from 'components/sections/admin-portal/server-errors/table';
import { TeamsTable } from 'components/sections/admin-portal/teams/table';
import { UsersTable } from 'components/sections/admin-portal/users/table';
import { VideosTableHoC } from 'components/sections/admin-portal/videos/table';
import { MachineModelsProvider } from 'contexts/admin/machine-models.context';
import { MachinesProvider } from 'contexts/admin/machines.context';
import { TeamsProvider } from 'contexts/admin/teams.context';
import { UsersProvider } from 'contexts/admin/users.context';
import { AuthContext } from 'contexts/auth.context';
import { SectionName, SubSectionName } from 'enums/route.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminTabNav } from './tab-nav';

const RedirectNav = () => (
  <Navigate
    to={RouteHelper.getSlug([SectionName.AdminPortal, SubSectionName.Users])}
    replace
  />
);

export const AdminPortal = () => {
  const { featureFlags } = useContext(AuthContext);
  const { sections } = featureFlags;

  return (
    <ErrorBoundary componentName="AdminPortal">
      <TeamsProvider>
        <MachinesProvider>
          <UsersProvider>
            <MachineModelsProvider>
              <Flex direction="column" gap={RADIX.FLEX.GAP.SM} height="100%">
                <AdminTabNav />
                <Routes>
                  <Route path="" element={<RedirectNav />} />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.Users])}
                    element={<UsersTable />}
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.Machines])}
                    element={<MachinesTable />}
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.Teams])}
                    element={
                      sections.adminTeams ? <TeamsTable /> : <RedirectNav />
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.ServerErrors])}
                    element={
                      sections.adminServerErrors ? (
                        <ServerErrorsTableHoC />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.MachineModels])}
                    element={
                      sections.adminMachineModels ? (
                        <MachineModelsTableHoC />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.BallTypes])}
                    element={
                      sections.adminBallTypes ? (
                        <BallTypesTable />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.ErrorTypes])}
                    element={
                      sections.adminErrorTypes ? (
                        <ErrorTypesTableHoc />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.StaticVideos])}
                    element={
                      sections.adminStaticVideos ? (
                        <VideosTableHoC />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.ApiKeys])}
                    element={
                      sections.adminApiKeys ? (
                        <ApiKeysManagementHoC />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route
                    path={RouteHelper.getSlug([SubSectionName.ServerControls])}
                    element={
                      sections.adminServerControls ? (
                        <ServerControlsTab />
                      ) : (
                        <RedirectNav />
                      )
                    }
                  />

                  <Route path="*" element={<RedirectNav />} />
                </Routes>
              </Flex>
            </MachineModelsProvider>
          </UsersProvider>
        </MachinesProvider>
      </TeamsProvider>
    </ErrorBoundary>
  );
};
